import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as AppActions from '../../../actions';
import * as AppConstants from '../../../constants';
import { getInDomain } from '../../../domains/externalUtils';
import * as AppErrors from '../../../lib/error';
import * as ListenerUtils from '../../../lib/ListenerUtils';
import { putAndWait } from '../../../sagas/TaskManager';
import Logger from '../../../tools/Logger';
import * as AppUtils from '../../../utils';

const log = Logger.child({ component: 'mediator/useCases/startStation' });

export function* isAPSEnabled() {
  return yield select(
    AppUtils.SourceType.isPandoraTypeAPSEnabled,
    AppConstants.SourceTypes.PandoraTypes.STATION,
  );
}

export function* playAPSSource({ payload: { stationId } }) {
  const pandoraId = yield select(getInDomain('playback.utils.getStationPandoraId'), stationId);
  yield put(getInDomain('playback.actions.playSource')({ pandoraId }));
}

export function* checkForStationRadio({ payload: { pandoraId } }) {
  if (pandoraId) {
    const annotations = yield select(AppUtils.Entity.getAnnotationsById, pandoraId);
    const isValidAnnotationType =
      annotations &&
      [
        AppConstants.SourceTypes.PandoraTypes.SONG,
        AppConstants.SourceTypes.PandoraTypes.ARTIST,
      ].some(type => type === annotations.get('type'));

    if (isValidAnnotationType && AppUtils.Rights.hasNoRadio(annotations)) {
      throw new AppErrors.RightsError('entity has no radio to start station');
    }
  }
}

export function* checkForStationRights({ payload: { pandoraId } }) {
  if (pandoraId) {
    const annotations = yield select(AppUtils.Entity.getAnnotationsById, pandoraId);
    if (
      annotations &&
      annotations.get('type') === AppConstants.SourceTypes.PandoraTypes.SONG &&
      AppUtils.Rights.isUnavailable(annotations)
    ) {
      throw new AppErrors.RightsError('insufficient rights to start station from this song');
    }
  }
}

export function* checkStationChangeInterruption({ payload: { stationId } }) {
  log.debug('startStation(): log STATION_CHANGE to stats & DRM');
  const previousSourceId = yield select(getInDomain('playback.utils.getCurrentPlaylist'));
  if (!previousSourceId) return;

  const previousIndex = yield select(
    getInDomain('playback.utils.getPlaylistCurrentIndex'),
    previousSourceId,
  );
  const previousTrack = yield select(
    getInDomain('playback.utils.getAudioMapFromPlaylistIndex'),
    previousSourceId,
    previousIndex,
  );
  const previousType = yield select(getInDomain('playback.utils.getCurrentPlaylistType'));

  if (previousTrack && stationId !== previousSourceId) {
    yield put(
      AppActions.Integrations.trackInterrupted({
        previousSourceId,
        previousType,
        previousTrack,
      }),
    );
  }
}

export function* setShuffleMode({ payload: { shouldShuffle } }) {
  if (shouldShuffle) {
    let shuffleId = yield select(getInDomain('playback.utils.getShuffleStationId'));
    if (!shuffleId) {
      yield call(putAndWait, AppActions.Api.ApiGetOrSetShuffleStations());
      shuffleId = yield select(getInDomain('playback.utils.getShuffleStationId'));
    }
    yield put(AppActions.Playback.setIsInShuffleMode(true));
    return { payload: { stationId: shuffleId } };
  }
  yield put(AppActions.Playback.setIsInShuffleMode(false));
}

export function* setOrCreateNextStation({ payload }) {
  yield call(putAndWait, {
    type: AppConstants.Playback.ActionTypes.SET_OR_CREATE_STATION,
    data: payload, // TODO-PAYLOAD: WEBCA-2505
  });
  const stationId = yield select(getInDomain('playback.utils.getNextStationId'));

  if (!stationId) {
    throw new AppErrors.PlaybackError('tried to start station with no id in startStation');
  }
  return { payload: { stationId } };
}

/*
 * Return an action for ChronosAdOpportunity to consume.
 * Needs to have a stationId in payload property.
 * Sometimes stationId changes in startStation after calling createStation.
 */
export function getChronosAction(
  payload,
  initialPlayback,
  dontIntercept,
  stationId,
  suppressAudioAds,
  suppressVideoAds,
  isResumingPlaylistTrack,
) {
  return {
    type: AppConstants.Playback.ActionTypes.SET_OR_CREATE_STATION,
    initialPlayback,
    dontIntercept,
    data: {
      ...payload,
      isResumingPlaylistTrack,
      stationId,
      suppressAudioAds,
      suppressVideoAds,
    },
  };
}

export function* shouldPlayAd({ payload }) {
  // We should make sure the ad queue is cleared before progressing
  yield call(getInDomain('audioAds.utils.clearAudioAdQueue', null));

  const newStation = yield select(getInDomain('playback.utils.getStationIsNew'), payload.stationId);
  const isLaunchingSpecialStationTrack =
    payload.createStationParams.isLaunchingSpecialStationTrack ?? false;
  const isAdvertiserStation = yield select(
    getInDomain('playback.utils.getStationIsAdvertiser'),
    payload.stationId,
  );
  const isResumingPlaylistTrack = yield select(
    getInDomain('playback.utils.isResumingPlaylistTrack'),
    payload.stationId,
  );
  const suppressAudioAds =
    (isResumingPlaylistTrack && !isLaunchingSpecialStationTrack) ||
    (payload.isInitialPlayback && !newStation) ||
    (newStation && !isAdvertiserStation) ||
    payload.suppressAudioAds;
  const suppressVideoAds =
    newStation || payload.isInitialPlayback || isAdvertiserStation || payload.suppressVideoAds;

  // we check to see if we should be playing a audio/video ad(s) before continuing
  if (!suppressAudioAds || !suppressVideoAds) {
    const adListTrack = yield select(
      getInDomain('playback.utils.getCurrentTrackForAdList'),
      payload.stationId,
    );
    log.debug('startStation(): making ad opportunity call', adListTrack);
    const chronosAction = getChronosAction(
      payload,
      payload.isInitialPlayback,
      payload.dontIntercept,
      payload.stationId,
      suppressAudioAds,
      suppressVideoAds,
      isResumingPlaylistTrack,
    );
    yield call(getInDomain('ads.utils.adOpportunity', null), chronosAction, adListTrack);
  }
  return { payload: { suppressAudioAds, suppressVideoAds } };
}

export function* setStationPlaylist({ payload }) {
  const { stationId, startingTrackToken, forceApi } = payload;
  yield call(
    putAndWait,
    getInDomain('playback.actions.stationSetPlaylist')({
      type: AppConstants.SourceTypes.SourceTypes.STATION,
      id: stationId,
      forceApi,
      startingTrackToken,
    }),
  );
}

export function* resetAPS() {
  yield put(getInDomain('playback.actions.resetAPS')());
}

export function* forceRerouteOrRerouteBasedOnTier({ payload }) {
  const { stationId, rerouteBasedOnTier, forceReroute } = payload;
  const reroutePath = yield select(AppUtils.Route.getNowPlayingReroutePath, stationId);
  const isBasic = yield select(ListenerUtils.shouldCurrentListenerGetAds);
  const isAnon = yield select(ListenerUtils.isAnonymousListener);

  if (reroutePath && (forceReroute || (rerouteBasedOnTier && (isBasic || isAnon)))) {
    yield put(push(reroutePath));
  }
}

export function* playTrackFromStation({ payload }) {
  yield call(
    putAndWait,
    getInDomain('playback.actions.playTrackFromStation')({
      doRegisterSkipTrack: false,
      id: payload.stationId,
      logPlaybackActionsAnalytics: false,
      next: !!payload.startingTrackToken || !!payload.forceNextTrack, // When hybridStations have startingTrackTokens they will fetch a new fragment with the token at the start, so we need to play next as if we're starting
      nextTrackForceNewPlaylist: false,
      starting: true,
      suppressAudioAds: payload.suppressAudioAds,
      suppressVideoAds: payload.suppressVideoAds,
    }),
  );
}
